import React, { useState, useEffect } from "react";
import { useOutletContext } from "react-router-dom";
import { NursingHome } from "@/hooks/use-nursing-home";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Plus, Calendar, Trash2, Edit2, X } from "lucide-react";
import { format } from "date-fns";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

type ContextType = { nursingHome: NursingHome };

interface NHEvent {
  id: string;
  title: string;
  description: string | null;
  event_date: string;
  start_time: string | null;
  end_time: string | null;
  location: string | null;
  booking_url: string | null;
  is_published: boolean;
}

const emptyEvent = {
  title: "",
  description: "",
  event_date: "",
  start_time: "",
  end_time: "",
  location: "",
  booking_url: "",
  is_published: true,
};

export default function EventsPage() {
  const { nursingHome } = useOutletContext<ContextType>();
  const [events, setEvents] = useState<NHEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState(emptyEvent);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (nursingHome?.nh_slug) fetchEvents();
  }, [nursingHome?.nh_slug]);

  const fetchEvents = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("nursing_home_events")
      .select("*")
      .eq("nursing_home_id", nursingHome.nh_slug)
      .order("event_date", { ascending: true });
    if (!error && data) setEvents(data);
    setLoading(false);
  };

  const openNew = () => {
    setEditingId(null);
    setForm(emptyEvent);
    setDialogOpen(true);
  };

  const openEdit = (e: NHEvent) => {
    setEditingId(e.id);
    setForm({
      title: e.title,
      description: e.description || "",
      event_date: e.event_date,
      start_time: e.start_time || "",
      end_time: e.end_time || "",
      location: e.location || "",
      booking_url: e.booking_url || "",
      is_published: e.is_published,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.title || !form.event_date) {
      toast.error("Title and date are required");
      return;
    }
    setSaving(true);
    const payload = {
      nursing_home_id: nursingHome.nh_slug,
      title: form.title,
      description: form.description || null,
      event_date: form.event_date,
      start_time: form.start_time || null,
      end_time: form.end_time || null,
      location: form.location || null,
      booking_url: form.booking_url || null,
      is_published: form.is_published,
    };

    if (editingId) {
      const { error } = await supabase
        .from("nursing_home_events")
        .update(payload)
        .eq("id", editingId);
      if (error) toast.error("Failed to update event");
      else toast.success("Event updated");
    } else {
      const { error } = await supabase
        .from("nursing_home_events")
        .insert(payload);
      if (error) toast.error("Failed to create event");
      else toast.success("Event created");
    }
    setSaving(false);
    setDialogOpen(false);
    fetchEvents();
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Delete this event?")) return;
    const { error } = await supabase.from("nursing_home_events").delete().eq("id", id);
    if (error) toast.error("Failed to delete");
    else {
      toast.success("Event deleted");
      fetchEvents();
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Events & Open Days</h1>
          <p className="text-muted-foreground">Promote open days and events to families</p>
        </div>
        <Button onClick={openNew}>
          <Plus className="h-4 w-4 mr-2" />
          Add Event
        </Button>
      </div>

      {events.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Calendar className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium">No events yet</h3>
            <p className="text-muted-foreground mb-4">Create your first event or open day to attract families</p>
            <Button onClick={openNew}>
              <Plus className="h-4 w-4 mr-2" />
              Create Event
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {events.map((event) => (
            <Card key={event.id}>
              <CardContent className="p-4 flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold">{event.title}</h3>
                    {!event.is_published && (
                      <span className="text-xs bg-muted px-2 py-0.5 rounded">Draft</span>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    {format(new Date(event.event_date), "EEEE, d MMMM yyyy")}
                    {event.start_time && ` · ${event.start_time}`}
                    {event.end_time && ` – ${event.end_time}`}
                  </p>
                  {event.description && (
                    <p className="text-sm mt-2">{event.description}</p>
                  )}
                  {event.location && (
                    <p className="text-xs text-muted-foreground mt-1">📍 {event.location}</p>
                  )}
                </div>
                <div className="flex gap-1">
                  <Button size="icon" variant="ghost" onClick={() => openEdit(event)}>
                    <Edit2 className="h-4 w-4" />
                  </Button>
                  <Button size="icon" variant="ghost" onClick={() => handleDelete(event.id)}>
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Event Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingId ? "Edit Event" : "New Event"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Title *</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Open Day 2026" />
            </div>
            <div>
              <Label>Date *</Label>
              <Input type="date" value={form.event_date} onChange={(e) => setForm({ ...form, event_date: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Start Time</Label>
                <Input type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} />
              </div>
              <div>
                <Label>End Time</Label>
                <Input type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} />
              </div>
            </div>
            <div>
              <Label>Description</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Tell families what to expect..." rows={3} />
            </div>
            <div>
              <Label>Location</Label>
              <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Main reception hall" />
            </div>
            <div>
              <Label>Booking URL</Label>
              <Input value={form.booking_url} onChange={(e) => setForm({ ...form, booking_url: e.target.value })} placeholder="https://..." />
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={form.is_published} onCheckedChange={(v) => setForm({ ...form, is_published: v })} />
              <Label>Published</Label>
            </div>
            <Button onClick={handleSave} disabled={saving} className="w-full">
              {saving ? "Saving..." : editingId ? "Update Event" : "Create Event"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
